import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { REQUEST } from '../../../express.tokens';
import { environment } from '../../../environments/environment';
import { PlatformService } from '../services/platform.service';
import { devDomain } from 'src/config/dev.domain';

@Injectable({
  providedIn: 'root',
})
export class HostRequestsInterceptor implements HttpInterceptor {
  private _platform = inject(PlatformService);
  private _injector = inject(Injector);

  /**
   * List of app hosts from which request urls should not be modified
   *
   * @private
   */
  private _ignoredHosts = [
    'https://localhost:4200',
    'https://www.allstarcasino.com',
    'https://stage.allstarcasino.com',
    'https://dev.allstarcasino.com',
    ...devDomain,
  ];

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const appUrl = `https://${this._getAppHost()}`;

    req = req.clone({
      url: req.params.has('crossdomain') ? req.url : this.overrideUrl(req.url, appUrl),
    });

    return next.handle(req);
  }

  /**
   * Get domain name
   */
  public getDomainName() {
    return this._getAppHost();
  }

  /**
   * Change provided url to depending on host url
   *
   * @param url
   * @param appUrl
   * @private
   */
  public overrideUrl(url: string, appUrl: string = `https://${this._getAppHost()}`): string {
    switch (true) {
      case this._isIgnoredHost(appUrl):
        return url;
      case this._isRequestToCms(url):
        return this._overrideCmsHost(url, appUrl, environment.cms_host);
      case this._isRequestToBetterix(url):
        return this._overrideBetterixHost(url, appUrl, environment.platform_graphql);
      case this._isRequestToMlh(url):
        return this._overrideMlhHost(url, appUrl, environment.mlh_host);
      case this._isErrorRequest(url):
        return this._overrideErrorHost(url);
      case this._isRequestToAssets(url):
        return this._overrideAssetsHost(url);
      default:
        return url;
    }
  }

  /**
   * Returns hostname on which app running
   *
   * @private
   */
  private _getAppHost(): string | undefined {
    return this._platform.isBrowser ? window.location.host : this._injector.get(REQUEST).headers.host;
  }

  /**
   * Returns true if request url is assets url
   *
   * @param url
   * @private
   */
  private _isRequestToAssets(url: string): boolean {
    return url.startsWith(environment.assets_host);
  }

  /**
   * Override request to Betterix
   * @private
   */
  private _overrideBetterixHost(url: string, host: string, ssHost: string): string {
    const hostName = host.replace(/https:\/\/www.|https:\/\/stage./g, '');
    const originalHost = url.replace(/https:\/\/platform.|https:\/\/stage-platform./g, '').replace('/api/graphql', '');
    return url.replace(originalHost, hostName);
  }

  /**
   * Override request to cms
   *
   * @private
   */
  private _overrideCmsHost(url: string, host: string, cmsHost: string): string {
    return url.replace(cmsHost, host)
      .replace('https://stage.', 'https://stage-api.')
      .replace('https://www.', 'https://api.');
  }

  /**
   * Returns true if request url is CMS url
   *
   * @param url
   * @private
   */
  private _isRequestToCms(url: string): boolean {
    return url.startsWith(environment.cms_host);
  }

  /**
   * Returns true if the request URL contains the 'graphql' string.
   * @param url - The URL of the request.
   * @returns True if the URL contains 'graphql', false otherwise.
   */
  private _isRequestToBetterix(url: string): boolean {
    return url.includes('graphql');
  }

  /**
   * Returns true if url should not be override
   *
   * @param host
   * @private
   */
  private _isIgnoredHost(host: string): boolean {
    if (host.includes('localhost') || host.includes('127.0.0.1')) {
      return true;
    }
    return this._ignoredHosts.includes(host);
  }

  /**
   * Returns true if request url is mlh url
   *
   * @param url
   * @private
   */
  private _isRequestToMlh(url: string): boolean {
    return url.startsWith(environment.mlh_host);
  }

  /**
   * Returns true if request url is mlh url
   *
   * @param url
   * @private
   */
  private _isErrorRequest(url: string): boolean {
    return url.startsWith(environment.error_host);
  }

  /**
   * Override request to mlh
   *
   * @private
   */
  private _overrideMlhHost(url: string, host: string, mlhHost: string): string {
    return url.replace(mlhHost, host).replace('https://www.', 'https://mlh.');
  }

  /**
   * Override request to SS
   *
   * @private
   */
  private _overrideErrorHost(url: string): string {
    const hostPrefix = environment.production ? 'www.' : 'stage.';
    const hostname = this.getDomainName().replace(hostPrefix, '');
    return url.replace(/\ballstarcasino\.com\b/g, hostname);
  }

  /**
   * Override request to assets
   *
   * @private
   */
  private _overrideAssetsHost(url: string): string {
    const hostPrefix = environment.production ? 'www.' : 'stage.';
    const hostname = this.getDomainName().replace(hostPrefix, '');
    return url.replace(/\ballstarcasino\.com\b/g, hostname);
  }
}
